import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    Container,
    Title,
    Text,
    Paper,
    PinInput,
    Divider,
    Stack,
    NativeSelect,
    LoadingOverlay,
    Button,
    Anchor, Checkbox
} from "@mantine/core";
import QRCodeScanner from "../../components/QRCodeScanner/v1/QRCodeScanner";
import { IconArrowLeft } from "@tabler/icons-react";
import { tracking } from "../../store/reducers/hamper";
import {nextStatusMapping, statusDisplay} from "../../components/Verification/HamperCodes";
import "./HamperStatusUpdate.css"

function HamperStatusUpdate() {
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from;
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.auth.role);
    const dashboardUri = `/${role?.toLowerCase()}`;
    const backPath = from === "customerHamperList" ? "/employee/customer-hamper-list" : dashboardUri;
    const backLabel = from === "customerHamperList" ? "Back to Customer Hamper List" : "Back to Dashboard";

    const statuses = [
        { label: statusDisplay["PICKED_UP_FROM_CUSTOMER"], value: "PICKED_UP_FROM_CUSTOMER" },
        { label: statusDisplay["DROPPED_OFF_AT_LAUNDROMAT"], value: "DROPPED_OFF_AT_LAUNDROMAT" },
        { label: statusDisplay["PICKED_UP_FROM_LAUNDROMAT"], value: "PICKED_UP_FROM_LAUNDROMAT" },
        { label: statusDisplay["DELIVERED_TO_CUSTOMER"], value: "DELIVERED_TO_CUSTOMER" },
    ];

    const [autoSubmit, setAutoSubmit] = useState(false);
    const [hamperCode, setHamperCode] = useState(searchParams.get("code") || "");
    const [formData, setFormData] = useState(nextStatusMapping[ searchParams.get("status") ] || "PICKED_UP_FROM_CUSTOMER");
    const [loading, setLoading] = useState(false);
    const pickupSkip = searchParams.get("status")  === "AWAITING_PICKUP" || searchParams.get("status") === "DELIVERED_TO_CUSTOMER"

    const handleVerification = ( status, codeOverride ) => {
        let request = {
            hamperCode,
            status,
        }

        if( codeOverride ) {
            request[ "hamperCode" ] = codeOverride;
        }

        if ( (hamperCode && status) || (codeOverride && status)) {
            const body = {
                body: request,
                setHamperCode,
                setLoading,
            };
            setLoading(true);
            dispatch(tracking(body));
        }
    };

    const handleInputChange = (value) => {
        setHamperCode(value.toUpperCase());

        if( autoSubmit && value.length === 5 ) {
            handleVerification( formData, value.toUpperCase() )

            setHamperCode( "" );
        }
    };

    const handleScanResult = (scannedCode) => {
        handleInputChange(scannedCode);
    };

    return (
        <Container size="xs" my="xl">
            <Anchor
                onClick={() => navigate(backPath)}
                c="blue"
                size="sm"
                mb="lg"
                underline
                style={{ display: "inline-flex", alignItems: "center" }}
            >
                <IconArrowLeft size={16} style={{ marginRight: 8 }} />
                {backLabel}
            </Anchor>

            <Paper withBorder shadow="md" radius="md" p={30} mt={50}>
                <LoadingOverlay
                    visible={loading}
                    zIndex={1000}
                    overlayProps={{ radius: "sm", blur: 2 }}
                />
                <Stack spacing="md" align="center">
                    <Title order={3}>Update Hamper Status</Title>
                    <Text size="sm" c="dimmed" align="center" mb="md">
                        Scan or enter the hamper code to update the hamper tracking status
                    </Text>

                    <NativeSelect
                        label="Status"
                        data={statuses.map((status) => ({
                            label: status.label,
                            value: status.value,
                        }))}
                        value={formData}
                        onChange={(e) => setFormData(e.target.value)}
                        required
                        className={"hamperStatusUpdateDropdown"}
                    />

                    <Divider labelPosition="center" />

                    <Stack spacing="md" align="center">
                        <PinInput
                            length={5}
                            value={hamperCode}
                            onChange={handleInputChange}
                            size="lg"
                            radius="md"
                            defaultValue=""
                            placeholder=""
                            styles={{
                                input: {
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                },
                            }}
                        />

                        <Divider label="OR" labelPosition="center" />
                        <QRCodeScanner handleScanResult={handleScanResult} />
                    </Stack>

                    <Checkbox checked={autoSubmit} label="Enable Auto Submit" onClick={() => setAutoSubmit( !autoSubmit )} />
                    { !autoSubmit &&
                        <Button
                            mt="md"
                            fullWidth
                            onClick={() => handleVerification( formData ) }
                            disabled={hamperCode.length !== 5 || loading}
                        >
                            Submit
                        </Button>
                    }
                    { pickupSkip &&
                        <Button
                            mt="md"
                            fullWidth
                            onClick={() => handleVerification("PICKUP_SKIPPED")}
                            disabled={hamperCode.length !== 5 || loading}
                            className={"hamperUpdatePickupSkipped"}
                        >
                            Skip Pickup
                        </Button>
                    }
                </Stack>
            </Paper>
        </Container>
    );
}

export default HamperStatusUpdate;